/**
 * Afrikaans: Menu and frame
 *
 * What this file covers: The side menu, its section headings, the top bar, the profile menu and the frame
 * around every page.
 *
 * Tone: One or two words per item, like a sign on a door. Nouns, not sentences. Every label must fit a narrow
 * menu. The words chosen here are the names of the pages everywhere else.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const menu: Catalog = {
  'nav.section.workspace': 'Werkspasie',
  'nav.section.audience': 'Gehoor',
  'nav.section.automation': 'Outomatisering',
  'nav.section.settings': 'Instellings',
  'nav.section.account': 'Rekening',
  'nav.dashboard': 'Paneelbord',
  'nav.inbox': 'Inkassie',
  'nav.contacts': 'Kontakte',
  'nav.broadcasts': 'Uitsendings',
  'nav.templates': 'Sjablone',
  'nav.flows': 'Vloeie',
  'nav.numbers': 'Nommers',
  'nav.billing': 'Fakturering',
  'nav.settings': 'Instellings',
  'nav.menu': 'Kieslys',
  'shell.org.tooltip': 'Jou organisasie. Open organisasie-instellings.',
  'nav.openMenu': 'Open navigasie',
  'nav.closeMenu': 'Sluit navigasie',
  'shell.wallet.aria': 'Beursiesaldo {{amount}}. Maak fakturering oop.',
  'shell.skipToContent': 'Slaan oor na hoofinhoud',
  'shell.loading': 'Laai jou werkruimte…',
  'shell.session.expiredTitle': 'Sessie het verstryk',
  'shell.session.expiredBody': 'Jy is afgeteken omdat jou sessie verstryk het. Teken weer in om voort te gaan.',
  'shell.realtime.connected': 'Gekoppel',
  'shell.realtime.connecting': 'Koppel tans…',
  'shell.realtime.disconnected': 'Aflyn',
  'shell.realtime.tooltip.connected': 'Lewendige opdaterings is aan: nuwe boodskappe kom outomaties in.',
  'shell.realtime.tooltip.connecting': 'Herkoppel tans aan lewendige opdaterings…',
  'shell.realtime.tooltip.disconnected': 'Lewendige opdaterings is af. Nuwe boodskappe sal nie verskyn nie totdat jy herkoppel.',
  'shell.realtime.reconnect': 'Herkoppel',
  'shell.profile.open': 'Open rekeningkieslys',
  'shell.profile.menu': 'Rekeningkieslys',
  'shell.profile.guest': 'Afgeteken',
  'shell.profile.account': 'Rekening',
  'shell.profile.allSettings': 'Alle instellings',
  'shell.localePicker': 'Verander taal',
  'shell.locale.partial': '{{label}} ({{percent}}% vertaal)',
  'shell.impersonation.title': 'Voordoening aktief',
  'shell.impersonation.body': 'Jy bekyk {{org}} as {{operator}}, vanaf die platformkonsole. Hierdie sessie eindig outomaties.',
  'shell.impersonation.exit': 'Beëindig voordoening',
  'shell.impersonation.exitAria': 'Beëindig voordoening en sluit hierdie oortjie',
  'shell.error.title': 'Iets het op hierdie bladsy verkeerd geloop',
  'shell.error.body': 'ʼn Onverwagte weergawefout het voorgekom.',
  'shell.error.retry': 'Probeer weer',
  'shell.error.reload': 'Herlaai bladsy',
  'shell.verifyEmail.title': 'Bevestig jou e-posadres',
  'shell.verifyEmail.body': 'Kyk by {{email}} vir die bevestigingskakel. Sommige funksies bly gesluit totdat jy dit bevestig.',
  'shell.verifyEmail.action': 'Stuur skakel weer',
  'nav.knowledge': 'Kennisbasis',
  'nav.compose': 'Stel op',
  'nav.developers': 'Ontwikkelaars',
  'nav.channels': 'Kanale',
  'nav.refer': 'Verwys en verdien',
  'nav.voice': 'Stem',
  'nav.section.bookings': 'Besprekings',
  'nav.bookings.calendar': 'Kalender',
  'nav.bookings.list': 'Besprekings',
  'nav.bookings.services': 'Dienste',
  'nav.bookings.staff': 'Personeel en ure',
  'nav.bookings.classes': 'Klasse',
  'nav.bookings.settings': 'Besprekingsinstellings',
  'nav.section.tickets': 'Kaartjies',
  'nav.tickets.queue': 'Tou',
  'nav.tickets.board': 'Bord',
  'nav.tickets.tasks': 'My take',
  'nav.tickets.reports': 'Verslae',
  'nav.tickets.settings': 'Kaartjie-instellings',
  'shell.assistant.title': 'Assistent',
  'shell.assistant.subtitle': 'Hulp met die konsole, op enige bladsy',
  'shell.assistant.open': 'Vra die assistent',
  'shell.assistant.close': 'Maak die assistent toe',
  'shell.assistant.clear': 'Begin oor',
  'shell.assistant.hello': 'Hallo. Vra my enigiets oor die konsole: waarvoor ’n bladsy is, waar om iets te kry, of hoe om dit te doen.',
  'shell.assistant.helloName': 'Hallo {{name}}. Vra my enigiets oor die konsole: waarvoor ’n bladsy is, waar om iets te kry, of hoe om dit te doen.',
  'shell.assistant.examples': 'Byvoorbeeld: "Waarvoor is hierdie bladsy?", "Neem my na my instellings" of "Skakel oor na donker modus". Ek vra altyd voordat ek iets doen.',
  'shell.assistant.placeholder': 'Vra ’n vraag of vra dat iets gedoen word',
  'shell.assistant.inputLabel': 'Jou boodskap aan die assistent',
  'shell.assistant.send': 'Stuur',
  'shell.assistant.thinking': 'Dink tans…',
  'shell.assistant.confirm': 'Ja, doen dit',
  'shell.assistant.decline': 'Nee dankie',
  'shell.assistant.actionDone': 'Gedoen.',
  'shell.assistant.actionDeclined': 'Nie gedoen nie.',
  'shell.assistant.done': 'Gedoen: {{action}}',
  'shell.assistant.failed': 'Die assistent kon nie nou antwoord nie. Probeer asseblief weer.',
  'shell.assistant.noCredit': 'Daar is nie genoeg krediet om die assistent te vra nie.',
  'shell.assistant.topUp': 'Laai op',
  'shell.assistant.cost': 'Elke boodskap gebruik ’n bietjie krediet, gehef as KI-gebruik.',
  'nav.aiTools': 'KI-gereedskap',
  'shell.assistant.hint': 'Vra my enigiets. Ek is hier om te help.',
  'shell.assistant.peekTitle': 'Hallo, ek is jou assistent',
  'shell.assistant.peekTitleName': 'Hallo {{name}}, ek is jou assistent',
  'shell.assistant.peekBody': 'Vra my enigiets. Ek kan \'n bladsy vind, verduidelik wat jy sien of jou deur \'n taak lei.',
  'shell.assistant.peekClose': 'Nie nou nie',
  'nav.insights': 'Insigte'
};

export default menu;
